import * as actionNames from './names';

export enum CitationTabDOMIds {
  CitationFormId = 'citation_generator_citation_form',
  TabControlsCitationStyle = 'citation_generator_citationSelectStyle',
  TabControlsContainer = 'citation_generator_citationTabControlsContainer',
  CitationListContainer = 'citation_generator_citationListContainer',
  CitationDeleteConfirmDialog = 'citation_generator_citationDeleteConfirmDialog',
  CitationFormDialogTitle = 'citation_generator_citationFormDialogTitle',
  CitationFormDialogContent = 'citation_generator_citationFormDialogContent',
}

export enum CitationTabTooltips {
  TabControlsCitationStyle = 'Change citation style',
}

export enum CitationSourceEnum {
  WEBSITE = 'Website',
  BOOK = 'Book',
  JOURNAL = 'Journal',
  // ARCHIVE = 'Archive',
}
export enum CitationStylesEnum {
  APA = 'APA',
  MLA = 'MLA',
  CHICAGO = 'CHICAGO',
}
export interface CitationGeneratorStates {
  isEditFormOpen: boolean;
  editFormDefaultValuse?: CitationData | null;
  citations: CitationData[];
  searchType: CitationSourceEnum;
  indexOfCitaionToBeEdit?: number | null;
  searchQuery?: string;
  isLoadingResults?: boolean;
  pageNo: number;
  isLastPage?: boolean;
  bibSearchQuery?: string;
  isInplaceCreateFormOpenedFromCowriter?: boolean;
  isSearchMode: boolean;
}
export interface Contributor extends ContributorDetails {
  contributorType: ContributorTypes;
  key: string;
}

export interface IDate {
  day?: number | string;
  month?: number | string;
  year?: number | string;
  time?: string;
}

export interface ICitedPages {
  start?: number | string;
  end?: number | string;
}

export interface ContributorDetails {
  suffix?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  literal?: string;
}

export interface IPublicationInfo {
  publishDate: IDate;
  accessDate: IDate;
  title?: string;
  websiteTitle?: string;
  subTitle?: string;
  url?: string;
  edition?: string;
  volume?: string;
  issue?: number | string;
  totalVolumes?: string;
  series?: string;
  publisher?: string;
  doi?: string;
  city?: string;
  state?: string;
  journalTitle?: string;
  citedPages: ICitedPages;
  isbn?: string;
}

export type AuthhorKeys = 'suffix' | 'firstName' | 'middleName' | 'lastName';

export interface CitationData {
  publicationInfo: IPublicationInfo;
  contributors: Contributor[];
  citationSource: CitationSourceEnum;
  ignore?: boolean;
  id: string | null; // this is mongo _id -> id
  refId: string; // this is refId for ui inline citation node reference
  isCloned?: boolean;
  isCited?: boolean;
}

type StyleKeys =
  | 'i' // italics
  | 'b' // bold
  | 'sub' // subscript
  | 'sup'; // superscript

export type FormattedCitationContent = Array<{
  text: string;
  style?: {
    // styleName represents the name of style applied to corresponding `text` value
    // Example: italic, superscript, subscript, bold
    [key in StyleKeys]?: boolean;
  };
}>;

export interface CitedData {
  // the `id` is same as the refId attached to each CitationData
  id: string;
  short: FormattedCitationContent;
  full: FormattedCitationContent;
  bibtex: string;
  json: string;
  // Generated on the frontend for showing numbering on citations
  citationNumber?: number;
}

export interface ICitationStyle {
  id: string;
  name: string;
  nameShort?: string;
}

export interface CitationGeneratorReduxState {
  citationList: CitationData[];
  citationStyle: CitationStylesEnum | string;
  citationsFromCowriter: CitationData[];
  citationStyles: Array<ICitationStyle>;
  citedDataById: { [id: string]: CitedData };
}

export enum ContributorTypes {
  AUTHORS = 'authors',
  EDITORS = 'editors',
  COMPILERS = 'compilers',
  TRANSLATORS = 'translators',
  ILLUSTRATORS = 'illustrators',
}

export interface UserOpenedInplaceCreateForm {
  type: typeof actionNames.OpenInplaceCreateForm;
  payload: Boolean;
}

export interface UserChangedCitationStyle {
  type: typeof actionNames.USER_CHANGED_CITATION_STYLE;
  payload: CitationStylesEnum | string;
}

export interface AddCitaionToList {
  type: typeof actionNames.ADD_CITATION_TO_LIST;
  payload: any;
}

export interface AddCitaionsToList {
  type: typeof actionNames.ADD_CITATIONS_TO_LIST;
  payload: CitationData[];
}

export interface RemoveCitationFromList {
  type: typeof actionNames.REMOVE_CITATION_FROM_LIST;
  payload: number;
}

export interface RemoveAllCitaion {
  type: typeof actionNames.RemoveAllCitaionList;
}

export interface IgnoreWarning {
  type: typeof actionNames.IgnoreWarning;
  payload: number;
}

export interface SetCitedData {
  type: typeof actionNames.SetCitedData;
  payload: CitedData[];
}

export interface UpdateCitedData {
  type: typeof actionNames.UpdateCitedData;
  payload: CitedData[];
}

export interface AddClonedCitedData {
  type: typeof actionNames.AddClonedCitedData;
  payload: {
    oldId: string;
    newId: string;
    citationNumber?: number;
  };
}

export interface RemoveCitedData {
  type: typeof actionNames.RemoveCitedData;
  payload: string;
}

export interface SetCitationStyles {
  type: typeof actionNames.SetCitationStyles;
  payload: ICitationStyle[];
}

export interface RebuildCitationNumbers {
  type: typeof actionNames.RebuildCitationNumbers;
}

export type CitationGeneratorAction =
  | {
      type: typeof actionNames.EDIT_MODAL_TOGGLE;
      payload: {
        editFormDefaultValuse?: CitationData;
        index?: number;
        open: boolean;
      };
    }
  | {
      type: typeof actionNames.CHANGED_CITATION_SOURCE;
      payload: CitationSourceEnum;
    }
  | {
      type: typeof actionNames.SetSearchQuery;
      payload: string;
    }
  | {
      type: typeof actionNames.USER_SEARCHED_QUERY;
      payload: string;
    }
  | {
      type: typeof actionNames.SetLoadingResults;
      payload: boolean;
    }
  | {
      type: typeof actionNames.RestoreToDefaultState;
    }
  | {
      type: typeof actionNames.SetIsLastPage;
      payload: boolean;
    }
  | {
      type: typeof actionNames.IncrementPageNo;
      payload?: boolean;
    }
  | {
      type: typeof actionNames.OpenInplaceCreateForm;
      payload: boolean;
    }
  | {
      type: typeof actionNames.ADD_CITATIONS_TO_SEARCH_RESULT;
      payload: any;
    }
  | {
      type: typeof actionNames.StartSearchMode;
    }
  | {
      type: typeof actionNames.StopSearchMode;
    };

export type CitationListAction =
  | AddCitaionToList
  | UserChangedCitationStyle
  | RemoveCitationFromList
  | UserChangedCitationStyle
  | RemoveCitationFromList
  | IgnoreWarning
  | RemoveAllCitaion
  | AddCitaionsToList
  | UserOpenedInplaceCreateForm
  | SetCitedData
  | UpdateCitedData
  | RemoveCitedData
  | AddClonedCitedData
  | SetCitationStyles
  | RebuildCitationNumbers;
